import { useEffect, useRef, useState } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { ChevronLeft, Eye, Lightbulb, Move, X } from "lucide-react";
import { toast } from "react-toastify";
import Button from "../ui/Button";
import { cropDataForImage } from "../../util/autoCropRect";

/**
 * The crop screen for a page or the cover photo (docs/design/HANDOFF.md,
 * "Crop (updated 2026-10-22)"): full screen on mobile and tablet, a dialog on
 * desktop. The whole photo stays in view; the frame (the page, always the
 * page's aspect ratio) is what moves and resizes: drag it, drag its corners,
 * or use the Zoom slider (bigger = a tighter frame). A hint pill on the photo
 * says how to move it. Controls sit right under the title: the "Worth a look"
 * note or the `tip`, then Zoom; then "Looks good" with "Replace photo"
 * centred under it. There's no rotating (removed 2026-10-26): the line-art
 * model draws sideways people badly (docs/design/HANDOFF.md).
 *
 * `onSave` receives { blob, url, cropData } like the old CropperModal and
 * resolves when saved.
 *
 * Only one layout is mounted at a time (phone/tablet or the desktop dialog):
 * the cropper holds its instance on a ref, and two mounted copies would leave
 * the ref on the hidden one, so the slider would do nothing.
 */
const PAGE_ASPECT = 8.75 / 11.5;
const DESKTOP = "(min-width: 1024px)";

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(DESKTOP).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP);
    const onChange = (e) => setDesktop(e.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, []);
  return desktop;
}

const PageCropScreen = ({
  open,
  image,
  imageUrl,
  cropData,
  pageNumber,
  flagged = false,
  onSave,
  onClose,
  onReplace,
  title: titleProp,
  tip = null,
  subject = 'page',
}) => {
  const isDesktop = useIsDesktop();
  const cropperRef = useRef(null);
  // The largest frame that fits the photo at the page's aspect ratio (zoom 100).
  const maxBoxRef = useRef({ width: 1, height: 1 });
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setReady(false);
    setFailed(false);
    setSaving(false);
    setZoom(100);
    setTouched(false);
    if (!imageUrl) return undefined;
    // Cropper.js never reports a decode failure; probe the source ourselves.
    let cancelled = false;
    const probe = new Image();
    probe.onerror = () => {
      if (!cancelled) setFailed(true);
    };
    probe.src = imageUrl;
    return () => {
      cancelled = true;
      probe.onerror = null;
      probe.src = "";
    };
  }, [open, imageUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const cropper = () => cropperRef.current?.cropper;

  /** The largest page-shaped frame that fits the photo as displayed. */
  const measureMaxBox = (c) => {
    const canvas = c.getCanvasData();
    const width = Math.min(canvas.width, canvas.height * PAGE_ASPECT);
    maxBoxRef.current = { width, height: width / PAGE_ASPECT };
  };

  /** Slider value for the frame's current size: 100 = as big as it can be, 300 = a third of that. */
  const zoomFromBox = (c) => {
    const box = c.getCropBoxData();
    const value = (maxBoxRef.current.width / Math.max(1, box.width)) * 100;
    return Math.round(Math.min(300, Math.max(100, value)));
  };

  /** Put the frame where the saved rectangle says, then read the slider off it. */
  const handleReady = (event) => {
    const c = event?.currentTarget?.cropper || cropper();
    if (!c) return;
    try {
      // A crop saved with a turn (from before rotating was removed) is ignored: with no way to turn it
      // back the page would be stuck sideways, so it opens upright at the default frame instead.
      if (cropData && !cropData.rotate) {
        const { naturalWidth, naturalHeight } = c.getImageData();
        const data = cropDataForImage(cropData, naturalWidth, naturalHeight);
        if (data) c.setData({ ...data, width: Math.max(1, data.width - 0.5), height: Math.max(1, data.height - 0.5) });
      }
      measureMaxBox(c);
      setZoom(zoomFromBox(c));
    } catch (err) {
      console.warn("Could not restore crop position:", err);
    }
    setReady(true);
  };

  /** Resize the frame around its centre, keeping it inside the photo. */
  const applyZoom = (value) => {
    setZoom(value);
    setTouched(true);
    const c = cropper();
    if (!c || !ready) return;
    try {
      const box = c.getCropBoxData();
      const canvas = c.getCanvasData();
      const width = maxBoxRef.current.width * (100 / value);
      const height = width / PAGE_ASPECT;
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      const left = Math.min(Math.max(cx - width / 2, canvas.left), canvas.left + canvas.width - width);
      const top = Math.min(Math.max(cy - height / 2, canvas.top), canvas.top + canvas.height - height);
      c.setCropBoxData({ left, top, width, height });
    } catch (err) {
      console.warn("Resizing the frame failed:", err);
    }
  };

  const save = async () => {
    const c = cropper();
    if (!c || !ready || saving) return;
    // Nothing moved on a photo that was never in doubt: just close.
    if (!touched && !flagged) {
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      const imageData = c.getImageData();
      if (!imageData?.naturalWidth || !imageData?.naturalHeight) throw new Error("The photo hasn't loaded yet.");
      const data = c.getData();
      if (!data || data.width <= 0 || data.height <= 0) throw new Error("The crop area is too small.");
      const canvas = c.getCroppedCanvas({ imageSmoothingEnabled: true, imageSmoothingQuality: "high" });
      if (!canvas || !canvas.width || !canvas.height) throw new Error("We couldn't crop this photo.");
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob || !blob.size) throw new Error("We couldn't crop this photo.");
      await onSave({
        blob,
        url: URL.createObjectURL(blob),
        cropData: { ...data, frame: { width: imageData.naturalWidth, height: imageData.naturalHeight } },
      });
    } catch (error) {
      console.error("Crop failed:", error);
      toast.error(error.message || "We couldn't save this crop. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const title = titleProp || `Adjust page ${pageNumber}`;

  const banner = flagged ? (
    <div className="flex items-start gap-2.5 bg-amber-50 px-4 py-2.5 text-sm leading-snug text-amber-950 md:items-center md:justify-center md:px-6 md:py-3 md:text-[15px] lg:items-start lg:justify-start lg:rounded-xl lg:border lg:border-amber-300 lg:px-3.5 lg:py-3 lg:text-sm">
      <Eye className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700 md:mt-0 lg:mt-0.5" aria-hidden="true" />
      <span>We weren't sure where to crop this one. Check that the frame is around what you want on {subject === "page" ? "this page" : "your cover"}.</span>
    </div>
  ) : null;

  const tipNote = tip ? (
    <div className="flex items-start gap-2.5 rounded-xl border border-md-pink-light-1 bg-md-pink-light-2 px-3.5 py-3 text-sm leading-snug text-[#4A4446] md:text-[15px] lg:text-sm">
      <Lightbulb className="mt-0.5 h-[18px] w-[18px] flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
      <span>{tip}</span>
    </div>
  ) : null;

  const cropArea = (
    <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-[#2B2527]">
      {failed ? (
        <div className="px-8 text-center text-white">
          <p className="font-semibold">We couldn't open this photo.</p>
          <p className="mt-1 text-sm text-white/80">Replace it with another photo, or a different copy of this one.</p>
        </div>
      ) : (
        <>
          {!ready ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center text-white/80" role="status">
              Loading photo…
            </div>
          ) : null}
          <Cropper
            src={imageUrl}
            style={{ height: "100%", width: "100%", opacity: ready ? 1 : 0.3, transition: "opacity 0.2s" }}
            aspectRatio={PAGE_ASPECT}
            guides
            ref={cropperRef}
            viewMode={1}
            dragMode="none"
            cropBoxMovable
            cropBoxResizable
            zoomable={false}
            toggleDragModeOnDblclick={false}
            autoCrop
            autoCropArea={1}
            responsive
            restore={false}
            checkCrossOrigin
            crossOrigin="anonymous"
            background={false}
            ready={handleReady}
            cropstart={() => setTouched(true)}
            cropend={() => {
              const c = cropper();
              if (c) setZoom(zoomFromBox(c));
            }}
          />
          <span className="pointer-events-none absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-[rgba(27,13,18,0.72)] px-3 py-[7px] text-[13px] font-semibold text-white md:top-4 md:px-3.5 md:py-2 md:text-[15px] lg:text-sm">
            <Move className="h-[15px] w-[15px] flex-shrink-0" aria-hidden="true" />
            {isDesktop ? "Click and drag the frame to move it" : "Tap and drag the frame to move it"}
          </span>
          <span className="pointer-events-none absolute inset-x-0 bottom-3.5 text-center text-[13px] text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.75)] md:bottom-[18px] md:text-[15px] lg:bottom-4 lg:text-sm">The area inside the frame becomes your {subject}.</span>
        </>
      )}
    </div>
  );

  const zoomInput = (
    <input
      id="page-crop-zoom"
      type="range"
      min="100"
      max="300"
      value={zoom}
      disabled={!ready || failed}
      onChange={(e) => applyZoom(Number(e.target.value))}
      className="h-11 min-w-0 flex-1 lg:h-9 lg:flex-none"
      style={{ accentColor: "#9E4D5F" }}
    />
  );
  const actions = (
    <div className="flex flex-col items-stretch gap-1.5">
      <Button size="lg" fullWidth onClick={save} busy={saving} disabled={!ready || failed} className="lg:!min-h-[52px] lg:!text-base">
        {saving ? "Saving" : "Looks good"}
      </Button>
      <Button variant="text" onClick={onReplace} disabled={saving} className="self-center md:!text-base lg:!text-[15px]">
        Replace photo
      </Button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[80] bg-white lg:flex lg:items-center lg:justify-center lg:bg-[rgba(27,13,18,0.5)] lg:p-6" role="dialog" aria-modal="true" aria-label={title}>
      {/* Mobile and tablet: full screen. */}
      {!isDesktop ? (
      <div className="flex h-full flex-col">
        <header className="flex h-14 flex-shrink-0 items-center justify-between border-b border-md-pink-light-1 bg-white px-1.5 md:h-16 md:px-3">
          <button type="button" aria-label="Close" onClick={onClose} className="flex h-11 w-11 items-center justify-center text-md-dark">
            <ChevronLeft className="h-6 w-6 md:hidden" aria-hidden="true" />
            <X className="hidden h-6 w-6 md:block" aria-hidden="true" />
          </button>
          <h1 className="m-0 font-sans text-[17px] font-bold text-md-dark md:text-[19px]">{title}</h1>
          <span className="w-11" aria-hidden="true" />
        </header>
        {banner ? <div className="flex-shrink-0 border-b border-amber-300">{banner}</div> : null}
        {cropArea}
        <div className="flex flex-shrink-0 flex-col gap-3 bg-white px-4 pb-[max(30px,env(safe-area-inset-bottom))] pt-3.5 md:gap-4 md:px-16 md:pb-7 md:pt-[18px]">
          {tipNote}
          <div className="flex items-center gap-2.5 md:gap-3.5">
            <label htmlFor="page-crop-zoom" className="w-11 text-sm font-semibold text-md-dark md:w-auto md:text-[15px]">
              Zoom
            </label>
            {zoomInput}
          </div>
          {actions}
        </div>
      </div>
      ) : (
      /* Desktop: dialog with the photo on the left and the controls on the right. */
      <section className="flex w-full max-w-[980px] overflow-hidden rounded-3xl bg-white shadow-[0_24px_60px_rgba(27,13,18,0.3)]">
        <div className="flex h-[600px] min-w-0 flex-[999_1_480px] flex-col">{cropArea}</div>
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-[18px] px-7 pb-7 pt-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="m-0 mt-1.5 font-heading text-[30px] font-normal leading-[1.1] text-md-dark">{title}</h2>
            <button type="button" aria-label="Close" onClick={onClose} className="-mr-3 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-md-muted">
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {banner}
          {tipNote}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="page-crop-zoom" className="text-[15px] font-semibold text-md-dark">
              Zoom
            </label>
            {zoomInput}
          </div>
          <div className="mt-1.5 border-t border-md-pink-light-1 pt-[18px]">{actions}</div>
        </div>
      </section>
      )}
    </div>
  );
};

export default PageCropScreen;
